import { useState, useCallback } from 'react';
import { apiUrl } from '../lib/api';
import Button from '../components/Button';

interface LoaderInfo {
  exists: boolean;
  size?: number;
  etag?: string;
  uploaded?: string;
  version?: string | null;
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [authError, setAuthError] = useState('');

  const [loaderInfo, setLoaderInfo] = useState<LoaderInfo | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const adminHeaders = useCallback(
    () => ({ 'x-admin-key': adminKey }),
    [adminKey]
  );

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await fetch(`${apiUrl('/admin/loader-info')}`, {
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error('Invalid admin key');
      const data = await res.json();
      setLoaderInfo(data);
      setAuthed(true);
    } catch {
      setAuthError('Invalid admin key');
    }
  };

  const refreshInfo = async () => {
    const res = await fetch(`${apiUrl('/admin/loader-info')}`, {
      headers: adminHeaders(),
    });
    const data = await res.json();
    setLoaderInfo(data);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setMessage('Please select a file');
      return;
    }
    setUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (version) formData.append('version', version);

      const res = await fetch(`${apiUrl('/admin/upload')}`, {
        method: 'POST',
        headers: adminHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      setMessage(`Upload successful: ${(data.size / 1024 / 1024).toFixed(2)} MB`);
      setFile(null);
      setVersion('');
      await refreshInfo();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete the current loader? Users will not be able to download.')) return;
    try {
      const res = await fetch(`${apiUrl('/admin/loader')}`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setMessage('Loader deleted');
      await refreshInfo();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold mb-2">Admin Panel</h1>
          <p className="text-muted text-sm">Enter admin key to access</p>
        </div>
        <form onSubmit={handleAuth} className="space-y-5">
          {authError && (
            <div className="bg-danger/10 border border-danger/30 rounded px-4 py-3 text-sm text-danger">
              {authError}
            </div>
          )}
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            className="w-full bg-surface border border-border rounded px-4 py-2.5 text-foreground placeholder:text-muted/50 focus:outline-none focus:border-primary transition-colors"
            required
            autoFocus
          />
          <Button type="submit" className="w-full" size="lg">
            Unlock
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <h1 className="font-display text-3xl font-bold mb-8">Admin Panel</h1>

      <div className="space-y-6">
        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Current Loader</h2>
          {loaderInfo?.exists ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">File</span>
                <code>exusiai-loader.exe</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Size</span>
                <span>{(loaderInfo.size! / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              {loaderInfo.version && (
                <div className="flex justify-between">
                  <span className="text-muted">Version</span>
                  <span>{loaderInfo.version}</span>
                </div>
              )}
              {loaderInfo.uploaded && (
                <div className="flex justify-between">
                  <span className="text-muted">Uploaded</span>
                  <span>{new Date(loaderInfo.uploaded).toLocaleString()}</span>
                </div>
              )}
              <div className="pt-3">
                <Button variant="danger" size="sm" onClick={handleDelete}>
                  Delete Loader
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted">No loader uploaded yet.</p>
          )}
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Upload New Loader</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-xs text-muted uppercase tracking-wide block mb-1.5">
                Loader file (.exe)
              </label>
              <input
                type="file"
                accept=".exe,.jar,.zip"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm text-muted file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-white file:cursor-pointer file:hover:bg-primary-hover"
                required
              />
            </div>
            <div>
              <label className="text-xs text-muted uppercase tracking-wide block mb-1.5">
                Version (optional)
              </label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. 1.0.0"
                className="w-full bg-surface border border-border rounded px-4 py-2.5 text-foreground placeholder:text-muted/50 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <Button type="submit" disabled={uploading || !file}>
              {uploading ? 'Uploading...' : 'Upload Loader'}
            </Button>
          </form>
        </div>

        {message && (
          <div className="bg-primary/10 border border-primary/30 rounded px-4 py-3 text-sm">
            {message}
          </div>
        )}
      </div>
    </div>
  );
}
